import t from 'react-translate';
import { config } from '@config/pendo.config.json';

import { Button } from 'react-bootstrap';

import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { cloneMentoringProgram } from 'redux/actions/mentoring-programs';
import useTimeZone from 'athena/hooks/use-time-zone';
import { convertDateTime } from 'athena/components/mentoring-program/home/participant/session-edit-mode/session-edit-mode';

import {
  CloneMentorshipProgramActionType,
  CloneMentorshipProgramWizardStep,
} from './state/types';
import { useCloneMentorshipProgram } from './state/context';

type CloneMentorshipProgramModalFooterProps = {
  onClose?: (payload?: { didStartClone?: boolean }) => void;
};

const CloneMentorshipProgramModalFooter = ({
  onClose,
}: CloneMentorshipProgramModalFooterProps) => {
  const { state, dispatch } = useCloneMentorshipProgram();
  const { ianaTimeZone } = useTimeZone();
  const reduxDispatch = useAppDispatch();

  const handleNext = () => {
    dispatch({ type: CloneMentorshipProgramActionType.NEXT_STEP });
  };

  const handleBack = () => {
    dispatch({ type: CloneMentorshipProgramActionType.PREV_STEP });
  };

  const isReviewStep = state.step === CloneMentorshipProgramWizardStep.REVIEW;
  const isFormValid = state.targetConfig.name.trim().length > 0;
  const dataQATag = config.pendo.athena.mentorshipProgram.cloneProgramModal;
  const cloneTranslations = t.MENTORING_PROGRAMS.PROGRAM_HEADER_MENU.CLONE;

  const getReleasedDateForPayload = () => {
    if (!state.targetConfig.releasedDate) {
      return undefined;
    }

    const convertedDate = convertDateTime({
      date: state.targetConfig.releasedDate,
      timeZone: ianaTimeZone,
      convert: false,
    });

    return convertedDate || undefined;
  };

  const handleStartClone = () => {
    if (!state.sourceProgram || state.isCloning) {
      return;
    }

    const adminUserIds = state.admins
      .filter((admin) => state.selectedAdmins[String(admin.id)])
      .map((admin) => admin.user.id);

    dispatch({
      type: CloneMentorshipProgramActionType.SET_CLONING_STATUS,
      payload: true,
    });

    wrapThunkAction(
      reduxDispatch(cloneMentoringProgram({
        id: state.sourceProgram.id,
        name: state.targetConfig.name.trim(),
        description: state.targetConfig.description,
        releasedDate: getReleasedDateForPayload(),
        adminUserIds,
      })),
    ).then(() => {
      reduxDispatch(addAlertMessage({
        type: AlertMessageType.SUCCESS,
        header: cloneTranslations.ALERT.SUCCESS_TITLE(),
        message: "Small programs take minutes, while larger ones with hundreds of guides/communications may take up to an hour. We will send you an email once it's ready.",
      }));
      onClose?.({ didStartClone: true });
    }).catch(() => {
      dispatch({
        type: CloneMentorshipProgramActionType.SET_CLONING_STATUS,
        payload: false,
      });
      reduxDispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: cloneTranslations.ALERT.ERROR_TITLE(),
        message: cloneTranslations.ALERT.ERROR_MESSAGE(),
      }));
    });
  };

  let primaryLabel = t.FORM.NEXT();

  if (isReviewStep) {
    primaryLabel = state.isCloning
      ? cloneTranslations.ACTIONS.CLONING()
      : cloneTranslations.ACTIONS.START_CLONE();
  }
  const primaryDataQA = isReviewStep ? dataQATag.startCloneButton : dataQATag.nextButton;
  const primaryOnClick = isReviewStep ? handleStartClone : handleNext;
  const primaryDisabled = !isFormValid || (isReviewStep && state.isCloning);

  return (
    <div className='d-flex buttons-section'>
      {state.step > 1 && (
        <Button
          variant='secondary'
          onClick={handleBack}
          data-qa={dataQATag.backButton}
          className='mr-2'
          disabled={state.isCloning}
        >
          {t.FORM.BACK()}
        </Button>
      )}
      <Button
        variant='primary'
        onClick={primaryOnClick}
        disabled={primaryDisabled}
        data-qa={primaryDataQA}
      >
        {primaryLabel}
      </Button>
    </div>
  );
};

export default CloneMentorshipProgramModalFooter;
